import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  collection,
  getCountFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import PageHeader from '../components/PageHeader';
import { Card } from '../components/Form';
import { useDialog } from '../components/Dialog';
import StatusBadge from '../components/StatusBadge';
import { adminRecomputePayBreakdown } from '../lib/api';
import { formatDateTime, money } from '../lib/format';

interface JobRow {
  id: string;
  status?: string;
  [key: string]: unknown;
}

// Mirrors JobStatus in packages/delivery_shared job_models.dart.
const STATUSES = [
  'all',
  'available',
  'assigned',
  'accepted',
  'enRoute',
  'pickedUp',
  'inTransit',
  'delivered',
  'cancelled',
  'disputed',
];

const PAGE_SIZE = 50;

export default function JobsPage() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('all');
  const [rows, setRows] = useState<JobRow[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [pageLimit, setPageLimit] = useState(PAGE_SIZE);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const { confirm, dialog } = useDialog();

  useEffect(() => {
    const base = collection(db, 'jobs');
    const q =
      statusFilter === 'all'
        ? query(base, orderBy('createdAt', 'desc'), limit(pageLimit))
        : query(
            base,
            where('status', '==', statusFilter),
            orderBy('createdAt', 'desc'),
            limit(pageLimit)
          );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setError(null);
        setRows(snap.docs.map((d) => ({ ...(d.data() as JobRow), id: d.id })));
      },
      (e) => setError(`Failed to load jobs: ${e.message}`)
    );
    return unsub;
  }, [statusFilter, pageLimit]);

  // Total for the active filter (approximate; refreshed on filter change).
  useEffect(() => {
    const base = collection(db, 'jobs');
    const q =
      statusFilter === 'all'
        ? query(base)
        : query(base, where('status', '==', statusFilter));
    let active = true;
    getCountFromServer(q)
      .then((snap) => {
        if (active) setTotalCount(snap.data().count);
      })
      .catch(() => {
        if (active) setTotalCount(null);
      });
    return () => {
      active = false;
    };
  }, [statusFilter]);

  const onRecompute = async (jobId: string) => {
    const ok = await confirm({
      title: 'Recompute pay breakdown',
      message: `Recompute pay breakdown for job ${jobId}? The new amount becomes payable on completion.`,
      confirmLabel: 'Recompute',
    });
    if (!ok) return;
    setBusy(jobId);
    try {
      const res = await adminRecomputePayBreakdown({ jobId });
      const b = (res.data.payBreakdown ?? {}) as Record<string, number>;
      const lines = Object.entries(b)
        .filter(([, v]) => typeof v === 'number' && v !== 0)
        .map(
          ([k, v]) =>
            `${k.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase()}: ${
              k === 'peakMultiplier' ? `${v}×` : money(v)
            }`
        )
        .join('\n');
      await confirm({
        title: 'Pay breakdown updated',
        message: lines || 'Breakdown recomputed.',
        confirmLabel: 'Done',
        cancelLabel: 'Close',
      });
    } catch (e) {
      setError(`Recompute failed: ${e instanceof Error ? e.message : e}`);
    } finally {
      setBusy(null);
    }
  };

  const customerName = (r: JobRow): string => {
    const info = r.customerInfo as Record<string, unknown> | undefined;
    const company = info?.companyName;
    const name = info?.name;
    if (typeof company === 'string' && company) return company;
    if (typeof name === 'string' && name) return name;
    return '—';
  };

  const jobPay = (r: JobRow): number | null => {
    const total = r.totalEarnings;
    const base = r.basePay;
    if (typeof total === 'number' && total > 0) return total;
    if (typeof base === 'number') return base;
    return null;
  };

  return (
    <>
      <PageHeader
        title="Jobs"
        subtitle="Driver-facing job offers and lifecycle."
      />
      {dialog}
      {error && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          {error}
        </div>
      )}
      <Card>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <label className="text-sm text-slate-600">
            Status:
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPageLimit(PAGE_SIZE);
              }}
              className="ml-2 rounded-md border border-slate-300 px-2 py-1 text-sm focus:border-brand-500 focus:outline-none"
            >
              {STATUSES.map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </label>
          <span className="text-xs text-slate-400">
            {totalCount != null
              ? `Showing ${rows.length.toLocaleString()} of ${Math.max(
                  totalCount,
                  rows.length
                ).toLocaleString()}`
              : `${rows.length} job${rows.length === 1 ? '' : 's'}`}
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Job</th>
                <th className="px-3 py-2 text-left">Created</th>
                <th className="px-3 py-2 text-left">Customer</th>
                <th className="px-3 py-2 text-right">Pay</th>
                <th className="px-3 py-2 text-right">Distance</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => navigate(`/jobs/${r.id}`)}
                  className="cursor-pointer border-t border-slate-100 hover:bg-slate-50/60"
                >
                  <td
                    className="px-3 py-2 font-mono text-xs text-slate-500"
                    title={`Job ${r.id}${r.orderId ? ` · Order ${r.orderId}` : ''}`}
                  >
                    {r.id.slice(0, 8)}…
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-xs text-slate-500">
                    {formatDateTime(r.createdAt)}
                  </td>
                  <td className="max-w-xs truncate px-3 py-2">
                    {customerName(r)}
                  </td>
                  <td className="px-3 py-2 text-right font-medium tabular-nums">
                    {money(jobPay(r))}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">
                    {typeof r.distance === 'number'
                      ? `${r.distance.toFixed(1)} mi`
                      : '—'}
                  </td>
                  <td className="px-3 py-2">
                    <StatusBadge status={r.status} />
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onRecompute(r.id);
                      }}
                      disabled={busy === r.id}
                      className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
                    >
                      Recompute pay
                    </button>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="px-3 py-8 text-center text-sm text-slate-500"
                  >
                    No jobs found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {(totalCount != null
          ? rows.length < totalCount
          : rows.length >= pageLimit) && (
          <div className="mt-4 text-center">
            <button
              onClick={() => setPageLimit((n) => n + PAGE_SIZE)}
              className="rounded-md border border-slate-300 px-4 py-1.5 text-sm font-medium text-slate-600 transition hover:bg-slate-50"
            >
              Load more
            </button>
          </div>
        )}
      </Card>
    </>
  );
}
